// Inventory Intelligence — hotspot drill-down data (mocked, deterministic).
// One helper per hotspot type. Each returns a well-structured detail payload
// with headline metrics, contributor tables, timeline chart data, AI narrative
// and NBAs — mirroring the shape used elsewhere in the Control Tower.

let _s = 88881;
function rnd() { _s = (_s * 1664525 + 1013904223) % 4294967296; return _s / 4294967296; }
function ri(a, b) { return Math.floor(rnd() * (b - a + 1)) + a; }
function formatValueShort(v) {
  if (typeof v !== "number") return v;
  if (v >= 1e9) return `₱${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `₱${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `₱${(v / 1e3).toFixed(1)}K`;
  return `₱${v}`;
}

// -------------------------------------------------------------------------
// KPI STRIP drills — keyed by label. Returns { title, subtitle, kpis[], chart[],
//   contributors[], drivers[], aiExplanation, confidence, nba[] }.
// -------------------------------------------------------------------------
function kpiDrillFor(label) {
  const base = {
    "Active SKUs": {
      title: "Active SKUs · Network",
      subtitle: "8,240 SKUs live across 4 categories · 5 warehouse clusters",
      kpis: [
        { label: "Active SKUs (total)", value: "8,240", delta: 0.021, status: "success" },
        { label: "A-class (top 20%)", value: "1,648", delta: 0.008, status: "success" },
        { label: "NPI (< 90 days)", value: "312", delta: 0.184, status: "info" },
        { label: "Phase-out candidates", value: "184", delta: -0.084, status: "warning" },
      ],
      contributors: [
        { name: "HVAC Compressors", value: 2140, share: 0.26 },
        { name: "Auto AC Parts", value: 1980, share: 0.24 },
        { name: "Refrigerant Gases", value: 1420, share: 0.17 },
        { name: "Consumables", value: 1180, share: 0.14 },
        { name: "Filters & Driers", value: 940, share: 0.11 },
        { name: "Tools & Gauges", value: 580, share: 0.07 },
      ],
      aiExplanation: "Active SKU count grew +2.1% MoM to 8,240 — driven by 312 NPI additions (Sanden 2026 series, R32 accessory line). 184 SKUs are on the phase-out review board (≥150 days idle at ≥60% of branches). Assortment complexity is trending healthy: A-class share held at 20% (target). Recommend accepting the Merchandising phase-out proposal to hold SKU count under 8,300 through Q2.",
      nba: [
        { title: "Approve 184-SKU phase-out list (Merchandising review)", impact: 4_800_000, owner: "Merchandising", urgency: "medium" },
        { title: "Lock NPI throttle at 40 new SKUs / month", impact: 1_200_000, owner: "Merchandising", urgency: "low" },
      ],
      confidence: 0.91,
    },
    "Stockout SKUs": {
      title: "Stockout SKUs · Live",
      subtitle: "312 A-class SKUs currently stockout at ≥1 branch",
      kpis: [
        { label: "Stockout SKUs (A-class)", value: "312", delta: -0.024, status: "warning" },
        { label: "Stockout Rate (network)", value: "4.7%", delta: -0.008, status: "success" },
        { label: "Branches with ≥5 stockouts", value: "18", status: "critical" },
        { label: "Lost-Sale Est. / Day", value: "₱2.4M", delta: -0.14, status: "warning" },
      ],
      contributors: [
        { name: "Freon R32 9.5kg — Cebu, Mandaue, Lapu-Lapu", value: 8, share: 0.12 },
        { name: "Sanden SD7H15 Compressor — 8 branches", value: 8, share: 0.12 },
        { name: "Inverter Compressor 1.5HP — Metro Manila cluster", value: 6, share: 0.09 },
        { name: "Rotary 1.0HP — Davao Matina", value: 4, share: 0.06 },
        { name: "Capella TM-16 — Iloilo", value: 3, share: 0.05 },
      ],
      aiExplanation: "Network stockout SKU count at 312 — improving 2.4% WoW. Root causes: (1) 3-week Capella shipment delay from Manila-South port congestion (58 SKUs affected); (2) demand-forecast underestimate for R32 due to unseasonal heat (42°C NCR heat index vs 36°C forecast); (3) MOQ policy on 8 A-class SKUs is above the ML-suggested reorder point (deliberate procurement decision). Recommend approving the 8 AI Auto-Replenishment moves and pushing 40% of NCR HVAC inventory from Pasig WH to sub-hubs.",
      nba: [
        { title: "Approve 8 AI Auto-Replenishment moves (₱14.2M, 96% conf)", impact: 14_200_000, owner: "Demand Planning", urgency: "critical" },
        { title: "Trigger emergency HVAC pull-forward from Pasig WH to NCR sub-hubs (12 branches)", impact: 6_400_000, owner: "Operations", urgency: "high" },
        { title: "Review MOQ policy for 8 A-class fast-movers with Procurement", impact: 3_200_000, owner: "Procurement", urgency: "medium" },
      ],
      confidence: 0.94,
    },
    "Overstock SKUs": {
      title: "Overstock SKUs · Working-Capital Drag",
      subtitle: "487 SKUs with DoH > 60 days at ≥1 branch",
      kpis: [
        { label: "Overstock SKUs", value: "487", delta: 0.004, status: "warning" },
        { label: "Locked Working Capital", value: "₱84M", delta: -0.02, status: "warning" },
        { label: "≥120 days idle (Dead-Stock)", value: "18 SKUs", status: "critical" },
        { label: "Pooled-Transfer Candidates", value: "42", status: "info" },
      ],
      contributors: [
        { name: "Consumables — Foam Filter Roll (Davao)", value: 142, share: 0.14 },
        { name: "Consumables — Manifold Gauge Dual (legacy, Aparri)", value: 108, share: 0.11 },
        { name: "Auto — Kia Picanto Idler Pulley (CDO)", value: 96, share: 0.10 },
        { name: "Refrig — Door Gasket 24in (Iloilo)", value: 82, share: 0.08 },
      ],
      aiExplanation: "Overstock share at 11.8% (target < 10%) — modestly above threshold, driven by 4 legacy consumables SKUs in low-velocity provincial branches. Networkwide, 42 SKUs qualify for pooled transfer to Pasig WH (₱18M releasable). The 18 SKUs ≥120 days idle (₱4.82M) are already flagged in the Dead-Stock Detector for the Fri weekly review. No A-class contamination.",
      nba: [
        { title: "Execute 42-SKU pooled-transfer manifest to Pasig WH", impact: 18_000_000, owner: "Operations", urgency: "high" },
        { title: "Approve Shopee 4.4 liquidation lane for 6 SKUs > 300d DoH", impact: 3_640_000, owner: "Merchandising", urgency: "medium" },
      ],
      confidence: 0.89,
    },
    "Dead-Stock Value": {
      title: "Dead-Stock Value · ≥120 Days Idle",
      subtitle: "18 SKUs locking ₱4.82M in working capital",
      kpis: [
        { label: "Locked Value", value: "₱4.82M", delta: -0.06, status: "warning" },
        { label: "SKU Count (≥120d)", value: "18" },
        { label: "Expected Recovery", value: "₱3.64M", delta: 0.28, status: "success" },
        { label: "Pooled Transfer / Liquidation Split", value: "12 / 6" },
      ],
      contributors: [
        { name: "Foam Filter Roll Eco — Davao Matina", value: 620_000, share: 0.13 },
        { name: "Door Gasket Magnetic 24in — Iloilo", value: 540_000, share: 0.11 },
        { name: "BMW 3-Series Discharge Hose — Butuan", value: 460_000, share: 0.10 },
        { name: "Chevrolet Sail Bracket Kit — Zamboanga", value: 420_000, share: 0.09 },
      ],
      aiExplanation: "Dead-Stock Detector holding ₱4.82M in inventory value locked for ≥120 days. 12 SKUs are routable via pooled transfer to Pasig WH (higher-velocity destinations identified); 6 SKUs (avg 300+ days DoH) are set for Shopee 4.4 liquidation at −22% discount. Expected total recovery ₱3.64M (75%). Zero A-class or B-class contamination — Detector is functioning as designed. Approve the pooled transfer manifest to release capital by end of Q1.",
      nba: [
        { title: "Approve full 18-SKU disposition plan (12 transfer + 6 liquidate)", impact: 3_640_000, owner: "Merchandising", urgency: "high" },
        { title: "Add BMW/Chevrolet legacy Auto SKUs to Phase-Out list", impact: 720_000, owner: "Merchandising", urgency: "medium" },
      ],
      confidence: 0.86,
    },
    "Auto-Replen Queue": {
      title: "AI Auto-Replenishment Queue",
      subtitle: "Pending suggestions from ML-001 v3.4 (Demand Forecast Auto)",
      kpis: [
        { label: "Pending Suggestions", value: "24" },
        { label: "Total Value", value: "₱48.6M", status: "info" },
        { label: "High-Confidence (≥85%)", value: "18 / 24", delta: 0.14, status: "success" },
        { label: "Avg Confidence", value: "89.4%", delta: 0.024, status: "success" },
      ],
      contributors: [
        { name: "4,800u Freon R32 → Pasig WH", value: 5_900_000, share: 0.12 },
        { name: "1,200u Sanden SD7H15 → Cebu WH", value: 18_400_000, share: 0.38 },
        { name: "2,400u Inverter 1.5HP → Pampanga WH", value: 6_200_000, share: 0.13 },
        { name: "18 additional cluster moves", value: 18_100_000, share: 0.37 },
      ],
      aiExplanation: "24 replenishment suggestions pending review with ₱48.6M total value. 18 are high-confidence (≥ 85%) and can be approved with one-click; 6 are medium-confidence (72-84%) requiring human review. All are MOQ-adjusted against supplier contracts. Approving all 18 high-confidence items today prevents 96% of the projected 30-day stockout risk. Cost of delaying by 1 week: ₱2.1M in additional lost sales.",
      nba: [
        { title: "One-click approve 18 high-confidence suggestions", impact: 30_500_000, owner: "Demand Planning", urgency: "high" },
        { title: "Route 6 medium-confidence items to Demand Planning review", impact: 18_100_000, owner: "Demand Planning", urgency: "medium" },
      ],
      confidence: 0.89,
    },
    "DSI": {
      title: "Days Sales of Inventory (DSI)",
      subtitle: "Network-weighted average · Rolling 90-day",
      kpis: [
        { label: "Network DSI", value: "38.7 d", delta: -0.04, status: "success" },
        { label: "Target Band", value: "30-45 d" },
        { label: "Best Category (Consumables)", value: "22.4 d", status: "success" },
        { label: "Worst Category (Refrig)", value: "54.1 d", status: "warning" },
      ],
      contributors: [
        { name: "Consumables", value: 22.4, share: 0.34 },
        { name: "Auto AC Parts", value: 32.1, share: 0.28 },
        { name: "HVAC Compressors", value: 38.4, share: 0.22 },
        { name: "Refrig Equipment", value: 54.1, share: 0.16 },
      ],
      aiExplanation: "Network DSI at 38.7 days — comfortably inside the 30-45 day healthy band, and improving −1.6 days WoW. Consumables lead at 22.4d (near-perfect rotation); Refrig lags at 54.1d and is dragging the network number. Root cause: 3 legacy Refrig SKUs (Door Gasket Magnetic, Cold-Chain Compressor 1/4HP, Refrig Fan Blade Assembly) have DoH > 90d in provincial branches. Executing the pooled-transfer manifest lifts network DSI to ~36.2d.",
      nba: [
        { title: "Rebalance 3 Refrig legacy SKUs across the network", impact: 8_400_000, owner: "Operations", urgency: "medium" },
      ],
      confidence: 0.92,
    },
  };
  return base[label];
}

// -------------------------------------------------------------------------
// ABC × XYZ MATRIX cell drills — one payload per cell code (AX..CZ).
// -------------------------------------------------------------------------
function abcXyzDrillFor(cell) {
  const skusByCell = {
    AX: [
      { sku: "Freon R32 9.5kg", velocity: "24k u/mo", value: 14_800_000, doh: 18, action: "Tight ROP · 96h lead-time review" },
      { sku: "Sanden SD7H15 Compressor", velocity: "6.4k u/mo", value: 24_100_000, doh: 22, action: "Auto-replen enabled · high confidence" },
      { sku: "Inverter Compressor 1.5HP", velocity: "5.2k u/mo", value: 18_600_000, doh: 24, action: "Auto-replen enabled" },
      { sku: "Copper Tubing 1/4\" 15m", velocity: "12k u/mo", value: 6_400_000, doh: 20, action: "Auto-replen enabled" },
    ],
    CZ: [
      { sku: "Manifold Gauge Dual (legacy)", velocity: "18 u/mo", value: 420_000, doh: 342, action: "Phase-out · liquidate Shopee 4.4" },
      { sku: "Charging Hose R134a (obsolete)", velocity: "12 u/mo", value: 380_000, doh: 298, action: "Phase-out · pooled transfer" },
      { sku: "Chevrolet Sail Bracket Kit", velocity: "4 u/mo", value: 420_000, doh: 267, action: "Phase-out · liquidate" },
    ],
    AY: [
      { sku: "Capella Auto TM-16", velocity: "3.2k u/mo (±38%)", value: 8_400_000, doh: 28, action: "Safety-stock 2 wk · forecast-driven" },
      { sku: "Panasonic Refrig 1/4HP", velocity: "1.8k u/mo (±42%)", value: 6_100_000, doh: 32, action: "Safety-stock 2 wk" },
    ],
    BX: [
      { sku: "Fan Capacitor 6µF", velocity: "1.2k u/mo (stable)", value: 480_000, doh: 24, action: "MOQ-adjusted reorder" },
      { sku: "Drier Filter 083", velocity: "820 u/mo (stable)", value: 420_000, doh: 26, action: "MOQ-adjusted reorder" },
    ],
  };
  const policies = {
    AX: "Highest-value + stable demand. Tight reorder discipline; MOQ tuned to demand; automate approvals.",
    AY: "High-value but variable. Safety stock 2 weeks; forecast-driven ROP; weekly review.",
    AZ: "High-value + volatile. Manual demand planner review; supplier collaboration on lead-times.",
    BX: "Medium-value, stable. MOQ-adjusted reorder; monthly review.",
    BY: "Medium-value, variable. Standard safety-stock; category-level pooling.",
    BZ: "Medium-value, volatile. Case-by-case; consider clustering.",
    CX: "Low-value, stable. Bulk order 3-4x/year; opportunistic pricing.",
    CY: "Low-value, variable. Consider consolidation; assortment review.",
    CZ: "Bottom-right corner — phase-out candidates. Confirm with sales; pooled transfer or liquidate.",
  };
  return {
    title: `${cell.code} Cell · ${cell.count} SKUs · ${formatValueShort(cell.value)}`,
    subtitle: policies[cell.code] || "AI-recommended policy",
    kpis: [
      { label: "SKUs in Cell", value: `${cell.count}` },
      { label: "Total Value", value: `₱${(cell.value / 1e6).toFixed(1)}M`, status: "info" },
      { label: "Avg Turnover", value: cell.code.startsWith("A") ? "12.4×" : cell.code.startsWith("B") ? "8.2×" : "3.6×" },
      { label: "Policy Compliance", value: `${(cell.compliance ? cell.compliance.overall * 100 : 60).toFixed(0)}%`, status: cell.compliance?.status || "warning" },
    ],
    compliance: cell.compliance ? [
      { label: "Auto-Replenishment Coverage",   value: cell.compliance.auto_replen,    target: cell.code.startsWith("A") ? 0.90 : cell.code.startsWith("B") ? 0.75 : 0.50, hint: "% of SKUs on ML-001 Auto-Replen" },
      { label: "Safety-Stock Adherence",        value: cell.compliance.safety_stock,   target: cell.code.startsWith("A") ? 0.90 : cell.code.startsWith("B") ? 0.75 : 0.60, hint: "% at or above ABC-XYZ recommended level" },
      { label: "MOQ Discipline (last 90d POs)", value: cell.compliance.moq_discipline, target: 0.85,                                                                     hint: "% of POs matching MOQ-adjusted ML suggestion" },
    ] : null,
    contributors: skusByCell[cell.code] || [
      { sku: "SKU list not exhibit-loaded — 8 representative sample only", velocity: "—", value: cell.value * 0.2, doh: 30, action: cell.policy },
    ],
    aiExplanation: buildAbcXyzNarrative(cell, policies[cell.code] || cell.policy),
    confidence: cell.code === "AX" ? 0.96 : cell.code === "CZ" ? 0.92 : 0.84,
    nba: buildAbcXyzNba(cell),
  };
}

function buildAbcXyzNarrative(cell, policyText) {
  const comp = cell.compliance;
  if (!comp) return `Cell ${cell.code} contains ${cell.count} SKUs worth ₱${(cell.value / 1e6).toFixed(1)}M. Policy: ${policyText}.`;

  const gapAr = 1 - comp.auto_replen;
  const gapSs = 1 - comp.safety_stock;
  const gapMq = 1 - comp.moq_discipline;
  const worst = gapAr >= gapSs && gapAr >= gapMq ? "auto-replenishment coverage"
              : gapSs >= gapMq ? "safety-stock adherence"
              : "MOQ discipline";

  if (cell.code === "AX") {
    return `Cell AX contains ${cell.count} SKUs worth ₱${(cell.value / 1e6).toFixed(1)}M — your best-managed inventory. Policy: ${policyText}. Compliance at ${(comp.overall * 100).toFixed(0)}% overall (auto-replen ${(comp.auto_replen * 100).toFixed(0)}%, safety-stock ${(comp.safety_stock * 100).toFixed(0)}%, MOQ ${(comp.moq_discipline * 100).toFixed(0)}%). Closing the remaining ${(gapAr * 100).toFixed(0)}pp on ${worst} pushes AX to near-perfect execution and releases ~₱${(cell.value * 0.03 / 1e6).toFixed(1)}M in working-capital efficiency.`;
  }
  if (cell.code === "CZ") {
    return `Cell CZ contains ${cell.count} SKUs worth ₱${(cell.value / 1e6).toFixed(1)}M — the network's dead-weight tail. Policy: ${policyText}. Compliance is deliberately low (${(comp.overall * 100).toFixed(0)}% overall) because these SKUs are candidates for phase-out. Low ${worst} at ${(comp[worst.split(" ")[0].replace("-", "_")] * 100 || 20).toFixed(0)}% reflects intentional under-investment. Approve the phase-out list to reclaim ₱${(cell.value * 0.72 / 1e6).toFixed(1)}M working capital.`;
  }
  return `Cell ${cell.code} contains ${cell.count} SKUs worth ₱${(cell.value / 1e6).toFixed(1)}M. Policy: ${policyText}. Compliance ${(comp.overall * 100).toFixed(0)}% overall — the biggest gap is ${worst} at ${(comp[worst.split(" ")[0].replace("-", "_")] * 100).toFixed(0)}% vs target. Closing this gap is the highest-ROI action in this cell.`;
}

function buildAbcXyzNba(cell) {
  const comp = cell.compliance;
  if (!comp) {
    return [
      cell.code === "AX" ? { title: "Extend ML-001 auto-replen coverage to remaining SKUs", impact: 8_400_000, owner: "Demand Planning", urgency: "medium" } :
      cell.code === "CZ" ? { title: "Approve CZ cell full phase-out — liquidate + pooled transfer", impact: 12_800_000, owner: "Merchandising", urgency: "high" } :
      { title: `Review ${cell.code} cell assortment quarterly`, impact: 2_400_000, owner: "Merchandising", urgency: "medium" },
    ];
  }
  const nbas = [];
  // Auto-replen gap
  if (comp.auto_replen < 0.85 && cell.code !== "CZ") {
    const gap = 1 - comp.auto_replen;
    nbas.push({
      title: `Extend ML-001 auto-replen coverage to remaining ${(gap * 100).toFixed(0)}% of ${cell.code} SKUs`,
      impact: Math.round(cell.value * gap * 0.06),
      owner: "Demand Planning",
      urgency: cell.code.startsWith("A") ? "high" : "medium",
    });
  }
  // Safety-stock gap
  if (comp.safety_stock < 0.85 && cell.code !== "CZ") {
    nbas.push({
      title: `Recalibrate safety-stock levels for ${Math.round(cell.count * (1 - comp.safety_stock))} ${cell.code} SKUs below recommendation`,
      impact: Math.round(cell.value * (1 - comp.safety_stock) * 0.04),
      owner: "Operations",
      urgency: "medium",
    });
  }
  // MOQ discipline gap
  if (comp.moq_discipline < 0.85 && cell.code !== "CZ") {
    nbas.push({
      title: `Enforce MOQ-adjusted POs — ${((1 - comp.moq_discipline) * 100).toFixed(0)}% of ${cell.code} orders deviate from ML suggestion`,
      impact: Math.round(cell.value * (1 - comp.moq_discipline) * 0.03),
      owner: "Procurement",
      urgency: "low",
    });
  }
  // CZ special case: phase-out
  if (cell.code === "CZ") {
    nbas.push({
      title: `Approve full CZ phase-out (${cell.count} SKUs) — liquidate + pooled transfer`,
      impact: Math.round(cell.value * 0.72),
      owner: "Merchandising",
      urgency: "high",
    });
  }
  // AX celebration path: automation extension
  if (cell.code === "AX" && comp.overall >= 0.9) {
    nbas.push({
      title: `Lock AX policy in ML-001 config — celebrate best-in-network execution`,
      impact: Math.round(cell.value * 0.02),
      owner: "Demand Planning",
      urgency: "low",
    });
  }
  return nbas.length ? nbas : [{ title: `Review ${cell.code} cell assortment quarterly`, impact: 2_400_000, owner: "Merchandising", urgency: "medium" }];
}

// -------------------------------------------------------------------------
// STOCKOUT CALENDAR row drills — SKU-specific 90-day timeline + branches
// -------------------------------------------------------------------------
function stockoutSkuDrillFor(row) {
  _s = Math.abs((row.skuId || "SKU").charCodeAt(0) * 137);
  const timeline = Array.from({ length: 90 }, (_, i) => {
    const state = row.days[i];
    return { day: `D-${89 - i}`, state, value: state === 3 ? 0 : state === 2 ? 4 : state === 1 ? 14 : 32 };
  });
  const affectedBranches = ["Cebu Mandaue", "Cebu Lapu-Lapu", "NCR Pasig Ortigas", "Davao Matina", "Iloilo Mandurriao", "Pampanga SF", "Cagayan de Oro", "Batangas City"].slice(0, ri(3, 6));
  const stockoutDays = row.days.filter(d => d >= 2).length;
  const criticalDays = row.days.filter(d => d === 3).length;
  return {
    title: `${row.name} · Stockout Timeline`,
    subtitle: `${row.skuId} · ${stockoutDays} stockout-days in last 90 days`,
    kpis: [
      { label: "Stockout Days (90d)", value: `${stockoutDays}`, status: stockoutDays > 20 ? "critical" : stockoutDays > 10 ? "warning" : "success" },
      { label: "Critical Days", value: `${criticalDays}`, status: criticalDays > 5 ? "critical" : "warning" },
      { label: "Affected Branches", value: affectedBranches.length },
      { label: "Est. Lost Sale", value: `₱${(stockoutDays * 0.14).toFixed(1)}M`, delta: -0.08, status: "warning" },
    ],
    timeline,
    contributors: affectedBranches.map(b => ({ name: b, days: ri(3, stockoutDays), value: ri(200_000, 1_400_000) })),
    aiExplanation: `${row.name} experienced ${stockoutDays} stockout-days in the last 90 days across ${affectedBranches.length} branches. The pattern correlates with the ${criticalDays > 5 ? "3-week Capella shipment delay (R²=0.71 with Manila-South port congestion)" : "unseasonal NCR heat index anomaly (42°C vs 36°C forecast)"}. Current safety stock (7 days) is under-sized against the demand variance observed. Auto-replen move already queued for ₱${(stockoutDays * 0.08).toFixed(1)}M value — recommend approving today to avoid a further ${Math.floor(stockoutDays * 0.6)}-day exposure over the next 4 weeks.`,
    confidence: 0.9,
    nba: [
      { title: "Approve queued auto-replen (safety-stock lift 7d → 14d)", impact: stockoutDays * 210_000, owner: "Demand Planning", urgency: "critical" },
      { title: "Escalate lead-time SLO with supplier", impact: 1_800_000, owner: "Procurement", urgency: "high" },
    ],
  };
}

// -------------------------------------------------------------------------
// DEAD-STOCK row drills — SKU-specific recovery scenario analysis
// -------------------------------------------------------------------------
function deadStockDrillFor(d) {
  const transferRecovery = Math.round(d.value * 0.86);
  const liquidateRecovery = Math.round(d.value * 0.64);
  const holdCost = Math.round(d.value * 0.18); // 18% annual carrying cost
  return {
    title: `${d.sku} · Recovery Scenario`,
    subtitle: `${d.branch} · ${d.region} · ${d.daysIdle} days idle · ${d.units} units`,
    kpis: [
      { label: "Inventory Value", value: `₱${(d.value / 1e6).toFixed(2)}M`, status: "warning" },
      { label: "Days Idle", value: `${d.daysIdle}d`, status: d.daysIdle > 300 ? "critical" : "warning" },
      { label: "Annual Carrying Cost", value: `₱${(holdCost / 1e6).toFixed(2)}M/yr`, status: "critical" },
      { label: "Recommended Recovery", value: `₱${((d.recommendation.startsWith("Liquidate") ? liquidateRecovery : transferRecovery) / 1e6).toFixed(2)}M`, status: "success" },
    ],
    scenarios: [
      { name: "Pooled Transfer → Pasig WH", recovery: transferRecovery, timeline: "5-8 days", risk: "Low", recommended: !d.recommendation.startsWith("Liquidate") },
      { name: "Shopee 4.4 Liquidation (−22%)", recovery: liquidateRecovery, timeline: "14-21 days", risk: "Medium", recommended: d.recommendation.startsWith("Liquidate") },
      { name: "Hold in place (do nothing)", recovery: 0, timeline: "N/A", risk: "High · ₱" + (holdCost / 1e6).toFixed(2) + "M/yr", recommended: false },
    ],
    aiExplanation: `Dead-stock analysis for ${d.sku} — currently ${d.daysIdle} days idle at ${d.branch}. Preferred disposition: ${d.recommendation}. ${
      d.recommendation.startsWith("Liquidate")
        ? `SKU velocity in the destination network is < 8u/month; pooled transfer would just relocate the problem. Shopee 4.4 liquidation at -22% captures 64% recovery in 14-21 days.`
        : `Pooled transfer identifies 3 destination branches with historical velocity ≥ ${d.units / 30 * 4}u/month. Expected 86% recovery in 5-8 days with minimal carrying cost.`
    } Cost of holding for one additional year: ₱${(holdCost / 1e6).toFixed(2)}M (18% carrying cost + obsolescence risk).`,
    confidence: 0.88,
    nba: [
      { title: `Execute ${d.recommendation}`, impact: d.recommendation.startsWith("Liquidate") ? liquidateRecovery : transferRecovery, owner: "Merchandising", urgency: "high" },
      { title: "Add to Phase-Out review list", impact: holdCost, owner: "Merchandising", urgency: "medium" },
    ],
  };
}

// -------------------------------------------------------------------------
// REPLEN QUEUE row drills — forecast breakdown, supplier context, risk
// -------------------------------------------------------------------------
function replenDrillFor(r) {
  return {
    title: `${r.sku} · Replenishment Detail`,
    subtitle: `${r.branch} · ${r.region} · Reason: ${r.reason}`,
    kpis: [
      { label: "Current On-Hand", value: `${r.currentUnits} u`, status: r.urgency === "critical" ? "critical" : "warning" },
      { label: "AI Suggested Qty", value: `${r.suggestedQty} u`, status: "info" },
      { label: "PO Value", value: `₱${(r.value / 1e6).toFixed(2)}M` },
      { label: "AI Confidence", value: `${(r.confidence * 100).toFixed(0)}%`, delta: 0.024, status: r.confidence >= 0.85 ? "success" : "warning" },
    ],
    forecast: Array.from({ length: 12 }, (_, i) => ({
      week: `W+${i + 1}`,
      demand: Math.round(r.suggestedQty / 8 * (1 + Math.sin(i / 2) * 0.2 + (rnd() - 0.5) * 0.15)),
      onHandProjected: Math.max(0, r.currentUnits - i * (r.suggestedQty / 12) + (i > 2 ? r.suggestedQty : 0)),
    })),
    contributors: [
      { name: "AI Forecast (baseline)", value: r.suggestedQty * 0.75, share: 0.75 },
      { name: "Safety-stock buffer (2wk)", value: r.suggestedQty * 0.18, share: 0.18 },
      { name: "MOQ round-up adjustment", value: r.suggestedQty * 0.07, share: 0.07 },
    ],
    supplierContext: {
      supplier: r.sku.includes("Sanden") ? "Sanden Philippines" : r.sku.includes("R32") ? "Refrig Gases Intl" : "Local OEM Network",
      leadTime: r.sku.includes("Sanden") ? "18-24 days" : "5-9 days",
      moq: r.sku.includes("Sanden") ? "480 u" : "48-120 u",
      otd: r.sku.includes("Sanden") ? "92%" : "88%",
    },
    aiExplanation: `AI Auto-Replen suggests ordering ${r.suggestedQty}u of ${r.sku} for ${r.branch} (₱${(r.value / 1e6).toFixed(2)}M). Confidence ${(r.confidence * 100).toFixed(0)}%. Trigger reason: ${r.reason}. The 12-week demand-projection chart shows on-hand hitting zero at W+${Math.max(1, Math.floor(r.currentUnits / (r.suggestedQty / 12)))}, well before the 18-24 day lead-time can respond without this reorder. Delaying by 1 week creates a ${(r.confidence * 100).toFixed(0)}%-probability stockout window of 5-7 days.`,
    confidence: r.confidence,
    nba: [
      { title: `Approve reorder (${r.suggestedQty}u, ₱${(r.value / 1e6).toFixed(2)}M)`, impact: r.value, owner: "Demand Planning", urgency: r.urgency },
      { title: "Trigger supplier lead-time confirmation", impact: r.value * 0.1, owner: "Procurement", urgency: "medium" },
    ],
  };
}

export function getInventoryDrill(type, payload) {
  if (type === "kpi") return kpiDrillFor(payload.label);
  if (type === "abcxyz") return abcXyzDrillFor(payload);
  if (type === "stockout-sku") return stockoutSkuDrillFor(payload);
  if (type === "deadstock") return deadStockDrillFor(payload);
  if (type === "replen") return replenDrillFor(payload);
  return null;
}
